import { css } from '@emotion/react'

import Tooltip from '@mui/material/Tooltip'
import Button, { ButtonProps } from '../Button'
import { ButtonVariant } from '../types'
import useTheme from '../../../../lib/hooks/useTheme'
import { baseColor, disabledIconColor, primaryTextColor } from '../../../../lib/styles/colors'
import { pullRightCss, srOnlyCss } from '../../../../lib/styles/cssStyles'

export enum ColorVariant {
  Disabled = 'disabled',
  Primary = 'primary',
  Secondary = 'secondary'
}

export type IconButtonProps = {
  /** ARIA compliant label */
  ariaLabel: string
  /** Optional aria-pressed attribute for toggle buttons */
  ariaPressed?: boolean
  /** Icon color variant */
  colorVariant?: ColorVariant
  /** Whether the button is disabled */
  disabled?: boolean
  /** NavLink prop -- used for '/' link  https://github.com/ReactTraining/react-router/issues/5118 */
  exact?: boolean
  /** Toggle tooltip display when hovering over the button (default: true) */
  displayTooltip?: boolean
  /** Optional override to indicate if a button is active */
  isActive?: boolean
  /** If the button links to an external source */
  isExternalLink?: boolean
  /** Whether the button is part of a Nav menu */
  isNav?: boolean
  /** Tooltip shown when hovering over the button.  If not set, ariaLabel will be used. */
  tooltip?: string
} & ButtonProps

// TODO: if we bring back disabled buttons, we'll need to add back
// height, width, and padding styles here.
const tooltipWrapperCss = css`
  display: inline-block;
`

const iconCss = css`
  width: 100%;
  display: flex;
  align-items: inherit;
  justify-content: inherit;
`

const iconWrapperCss = css`
  display: flex;
  align-items: inherit;
  justify-content: inherit;

  /* Ensure icon is hidden from accessibility tree since we provide text label */
  & > svg {
    aria-hidden: true;
  }
`

const IconButton = ({
  ariaLabel,
  children,
  color,
  colorVariant,
  disabled = false,
  displayTooltip = true,
  isActive = false,
  isExternalLink = false,
  pullRight,
  tooltip = '',
  ...props
}: IconButtonProps) => {
  const { primaryColor } = useTheme()
  const getColorVariant = () => {
    if (disabled) {
      return disabledIconColor
    }

    switch (colorVariant) {
      case ColorVariant.Primary:
        return primaryColor!
      case ColorVariant.Secondary:
        return primaryTextColor
      default:
        return baseColor
    }
  }

  const iconColor = color || getColorVariant()

  // Button content includes both icon and visually-hidden text for accessibility
  // This ensures buttons work with high contrast mode, custom stylesheets, and when CSS is disabled
  // Per WCAG 1.1.1, 1.4.1, and 4.1.2
  const buttonContent = (
    <span css={iconCss}>
      <span css={iconWrapperCss} aria-hidden="true">
        {children}
      </span>
      <span css={srOnlyCss}>{ariaLabel}</span>
    </span>
  )

  // Have to add the extra wrapper around the button to trigger the tooltip
  // then an extra wrapper so we can add the tab index to the span so it's
  // triggered on disabled buttons when tabbing to it with the keyboard
  return displayTooltip ? (
    // Wrap in span instead of div to avoid MUI adding invalid aria-label to div
    <Tooltip
      title={tooltip || ariaLabel}
      describeChild
      PopperProps={{
        // Disable portal to ensure tooltip is rendered within the landmark hierarchy
        disablePortal: true
      }}
    >
      <span css={[tooltipWrapperCss, pullRight && pullRightCss]}>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
        <span css={tooltipWrapperCss} tabIndex={disabled ? 0 : -1}>
          <Button
            {...props}
            ariaLabel={ariaLabel}
            color={iconColor}
            disabled={disabled}
            isActive={isActive}
            isExternalLink={isExternalLink}
            variant={ButtonVariant.Icon}
          >
            {buttonContent}
          </Button>
        </span>
      </span>
    </Tooltip>
  ) : (
    <div css={[tooltipWrapperCss, pullRight && pullRightCss]}>
      <Button
        {...props}
        ariaLabel={ariaLabel}
        color={iconColor}
        disabled={disabled}
        isActive={isActive}
        isExternalLink={isExternalLink}
        variant={ButtonVariant.Icon}
      >
        {buttonContent}
      </Button>
    </div>
  )
}

export default IconButton
